import { Box, Button, Flex, SegmentedControl, TextInput } from "@dehub/theme";
import { MagnifyingGlassIcon, PlusIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { WarehouseApi } from "@/apis";
import bz from "@/features/business/business.module.css";
import type { IWarehouseListRow } from "@/models/Warehouse";
import { ReasonTooltip } from "@/shared/a11y/reason-tooltip";
import { MissingPermission } from "@/shared/a11y/trans-code";
import { PageLayout } from "@/shared/layout";
import { WarehouseDrawer } from "./components/WarehouseDrawer";
import { WarehouseTable } from "./components/WarehouseTable";
import { createWarehouseColumns, isWarehouseActive } from "./components/warehouse-columns";
import { useWarehouseActions } from "./hooks/useWarehouseActions";
import { useWarehousePermissions } from "./hooks/useWarehousePermissions";

type StatusFilter = "all" | "on" | "off";

const SEARCH_DEBOUNCE_MS = 300;
// A partner has a handful of warehouses: the whole list is loaded once so the status tabs can show counts.
const LIST_LIMIT = 500;

export function WarehousePage() {
  const { t } = useTranslation("warehouse");
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const perms = useWarehousePermissions();
  const actions = useWarehouseActions();

  // Status (?status=), search text (?q=) and page (?page=) live in the URL: a reload or shared link lands on the same list.
  const statusParam = params.get("status");
  const status: StatusFilter = statusParam === "on" || statusParam === "off" ? statusParam : "all";
  const query = params.get("q") ?? "";
  const page = Math.max(1, parseInt(params.get("page") ?? "1", 10) || 1);
  const updateParams = (patch: Record<string, string | null>, replace = false) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value === "") next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace },
    );

  const [queryInput, setQueryInput] = useState(query);
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = queryInput.trim();
      if (next !== query) updateParams({ q: next || null, page: null }, true);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryInput]);
  useEffect(() => setQueryInput(query), [query]);

  const { data, error, isLoading, mutate } = useSWR(
    perms.list.hasAccess ? ["partner-warehouses", query] : null,
    () => WarehouseApi.list({ filter: { query }, offset: { page: 1, limit: LIST_LIMIT } }) as Promise<{ rows?: IWarehouseListRow[] }>,
    { revalidateOnFocus: false },
  );
  const all = useMemo(() => data?.rows ?? [], [data]);
  const counts = { all: all.length, on: all.filter(isWarehouseActive).length, off: all.filter((r) => !isWarehouseActive(r)).length };
  const rows = all.filter((r) => status === "all" || (status === "on" ? isWarehouseActive(r) : !isWarehouseActive(r)));

  const columns = useMemo(
    () =>
      createWarehouseColumns({
        perms,
        t,
        tCommon,
        onEdit: (row) => navigate(`/our-warehouse/${row.id}?mode=edit`),
        onStaff: (row) => navigate(`/our-warehouse/${row.id}?tab=staff`),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [perms.edit.hasAccess, perms.detail.hasAccess, t, tCommon],
  );

  const addReason = perms.add.hasAccess ? null : <MissingPermission code={perms.codes.add} />;
  const addButton = (
    <ReasonTooltip key="add" reason={addReason}>
      <Button className={bz.btn} leftSection={<PlusIcon size={16} weight="bold" />} onClick={actions.openAdd}>
        {t("addBtn")}
      </Button>
    </ReasonTooltip>
  );

  return (
    <PageLayout
      title={t("pageTitle")}
      subTitle={t("subTitle")}
      breadcrumb={[{ to: "/", label: tCommon("home") }, { label: t("pageTitle") }]}
      extra={[addButton]}>
      {perms.list.hasAccess ? (
        <Flex direction="column" gap="md" className={`${bz.tokens} ${bz.page}`}>
          <Box className={bz.toolbar}>
            <TextInput
              className={bz.search}
              aria-label={t("list.searchLabel")}
              type="search"
              name="q"
              autoComplete="off"
              placeholder={t("list.searchPlaceholder")}
              leftSection={<MagnifyingGlassIcon size={16} aria-hidden="true" />}
              value={queryInput}
              onChange={(event) => setQueryInput(event.currentTarget.value)}
            />
            <SegmentedControl
              aria-label={t("list.statusLabel")}
              value={status}
              onChange={(next) => updateParams({ status: next === "all" ? null : next, page: null })}
              classNames={{ root: bz.segmentRoot, indicator: bz.segmentIndicator, label: bz.segmentLabel }}
              data={[
                { value: "all", label: `${t("list.statusAll")} ${counts.all}` },
                { value: "on", label: `${tCommon("status.ACTIVE")} ${counts.on}` },
                { value: "off", label: `${tCommon("status.INACTIVE")} ${counts.off}` },
              ]}
            />
          </Box>
          <WarehouseTable
            rows={rows}
            columns={columns}
            loading={isLoading}
            loadError={error ? ((error as { message?: string }).message ?? tCommon("error")) : null}
            onRetry={() => mutate()}
            filtered={query.length > 0 || status !== "all"}
            onClearFilters={() => {
              setQueryInput("");
              updateParams({ q: null, status: null, page: null });
            }}
            page={page}
            onPageChange={(next) => updateParams({ page: next > 1 ? String(next) : null })}
            onAdd={actions.openAdd}
            addReason={addReason}
          />
        </Flex>
      ) : (
        perms.list.accessDenied()
      )}

      <WarehouseDrawer
        opened={actions.drawerOpen}
        perms={perms}
        onClose={actions.closeAll}
        onReload={() => mutate()}
      />
    </PageLayout>
  );
}
